import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import FilledButton from '../../components/Buttons/FilledButton';
import OutlinedButton from '../../components/Buttons/OutlinedButton';
import checkIcon from '../../assets/images/view-meeting/checkmark.svg';
import playIcon from '../../assets/images/my-meetings/play.svg';
import usersIcon from '../../assets/images/my-meetings/users.svg';
import checklistIcon from '../../assets/images/text-editor/ordered.svg';
import EmptyFolderMessage from '../../components/Library/EmptyFolderMessage';
import DotsLoader from '../../components/DotsLoader';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import TrainingPathAssignModal from '../../modals/TrainingPathAssignModal';
import Tooltip from '../../components/Tooltip';
import TrainingPathService from '../../services/TrainingPathService';
import OrganizationService from '../../services/OrganizationService';
import UserService from '../../services/UserService';
import { useUiStore } from '../../store';
import { UiContext } from '../../context/UiContext';

import {
  getTrainingPathStatusLabel,
  getTrainingPathStatusToken,
  TRAINING_PATH_STATUSES,
} from '../../constants/main';
import classes from './styles.module.scss';

const getStatusToken = (value) => getTrainingPathStatusToken(value);

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleDateString();
};

export default function TrainingPathDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const acl = searchParams.get('acl') || 'admin';
  const isAdminView = acl === 'admin';

  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );

  useEffect(() => {
    setBreadcrumbsOptions([
      { label: 'Training Paths', to: '/training-paths' },
      { label: 'Details', to: '#' },
    ]);
  }, [setBreadcrumbsOptions]);

  const { data, isLoading, error, refetch } = useQuery(
    ['training-path', id, acl],
    () => TrainingPathService.getTrainingPath(id, acl),
    {
      enabled: Boolean(id),
      retry: (failureCount, retryError) => {
        // Don't retry on 403 (Forbidden) errors
        if (retryError?.response?.status === 403) {
          return false;
        }
        // Retry other errors up to 3 times (default)
        return failureCount < 3;
      },
    }
  );

  const trainingPath = data?.trainingPath;
  const assignments = trainingPath?.Assignments || [];
  const content = data?.content || [];
  const activeAssignment = data?.activeAssignment || null;
  const traineeStatus =
    getTrainingPathStatusLabel(activeAssignment?.status) || 'Assigned';

  const rawSummaryStatus = isAdminView
    ? trainingPath?.status
    : activeAssignment?.status;
  const summaryStatusLabel =
    rawSummaryStatus
      ? getTrainingPathStatusLabel(rawSummaryStatus)
      : traineeStatus || getTrainingPathStatusLabel(trainingPath?.status) || '—';
  const summaryStatusToken = getStatusToken(
    rawSummaryStatus || summaryStatusLabel
  );
  const canTraineePlayAll =
    !isAdminView &&
    (summaryStatusToken === 'completed' || summaryStatusToken === 'approved');

  const { notifyError, notifySuccess } = useContext(UiContext);
  const [pendingAssignmentId, setPendingAssignmentId] = useState(null);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [resetAssignmentId, setResetAssignmentId] = useState(null);
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [approveAssignmentId, setApproveAssignmentId] = useState(null);
  const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);

  const { data: currentUser } = useQuery('me', UserService.getMe);
  const organizationId = currentUser?.organizationId;

  const { data: organizationUsers = [] } = useQuery(
    ['organization-users', organizationId],
    () => OrganizationService.getOrganizationUsers(organizationId),
    {
      enabled: Boolean(organizationId) && isAdminView,
    }
  );

  const trainingPathOptions = useMemo(() => {
    if (!trainingPath) return [];
    return [{ value: trainingPath.id, label: trainingPath.name }];
  }, [trainingPath]);


  const handleApproveRequest = useCallback((assignmentId) => {
    setApproveAssignmentId(assignmentId);
    setIsApproveModalVisible(true);
  }, []);

  const handleApproveConfirm = useCallback(async () => {
    if (!approveAssignmentId) {
      return;
    }
    await TrainingPathService.updateAssignmentStatus({
      assignmentId: approveAssignmentId,
      status: 'approved',
    });
    setIsApproveModalVisible(false);
    setApproveAssignmentId(null);
    refetch();
  }, [approveAssignmentId, refetch]);

  const handleApproveClose = useCallback(() => {
    setIsApproveModalVisible(false);
    setApproveAssignmentId(null);
  }, []);

  // Get trainee name for approval modal
  const approveTraineeName = useMemo(() => {
    if (!approveAssignmentId) return '';
    const assignment = assignments.find(a => a.id === approveAssignmentId);
    return assignment?.Trainee?.name || 'this trainee';
  }, [approveAssignmentId, assignments]);

  const handleCancelRequest = useCallback((assignmentId) => {
    setPendingAssignmentId(assignmentId);
    setIsReassigning(false);
    setIsCancelModalVisible(true);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    if (!pendingAssignmentId) {
      return;
    }
    await TrainingPathService.updateAssignmentStatus({
      assignmentId: pendingAssignmentId,
      status: 'canceled',
    });
    setIsCancelModalVisible(false);
    setPendingAssignmentId(null);
    setIsReassigning(false);
    refetch();
  }, [pendingAssignmentId, refetch]);

  const handleReassignRequest = useCallback((assignmentId) => {
    setPendingAssignmentId(assignmentId);
    setIsReassigning(true);
    setIsCancelModalVisible(true);
  }, []);

  const handleReassignConfirm = useCallback(async () => {
    if (!pendingAssignmentId) {
      return;
    }
    await TrainingPathService.updateAssignmentStatus({
      assignmentId: pendingAssignmentId,
      status: 'assigned',
    });
    setIsCancelModalVisible(false);
    setPendingAssignmentId(null);
    setIsReassigning(false);
    refetch();
  }, [pendingAssignmentId, refetch]);

  const handleCancelClose = useCallback(() => {
    setIsCancelModalVisible(false);
    setPendingAssignmentId(null);
    setIsReassigning(false);
  }, []);

  const handleResetRequest = useCallback((assignmentId) => {
    setResetAssignmentId(assignmentId);
    setIsResetModalVisible(true);
  }, []);

  const handleResetConfirm = useCallback(async () => {
    if (!resetAssignmentId) {
      return;
    }
    await TrainingPathService.resetAssignmentProgress(resetAssignmentId);
    setIsResetModalVisible(false);
    setResetAssignmentId(null);
    refetch();
  }, [resetAssignmentId, refetch]);

  const handleResetClose = useCallback(() => {
    setIsResetModalVisible(false);
    setResetAssignmentId(null);
  }, []);

  const handleModalAssign = useCallback(
    async ({ userIds, trainingPathIds }) => {
      if (!userIds.length || !trainingPathIds.length) {
        return;
      }

      try {
        await Promise.all(
          trainingPathIds.map((pathId) =>
            TrainingPathService.assignUsers(pathId, userIds)
          )
        );
        notifySuccess('Trainees assigned successfully.');
        setIsAssignModalOpen(false);
        refetch();
      } catch (assignError) {
        console.error(assignError);
        notifyError(
          assignError?.response?.data?.message ||
            'Unable to assign trainees. Please try again.'
        );
      }
    },
    [notifyError, notifySuccess, refetch]
  );

  const summaryMetrics = useMemo(() => {
    if (!trainingPath) {
      return [];
    }
    const videoCount = content.length;
    if (isAdminView) {
      const averageProgress = assignments.length
        ? Math.round(
            assignments.reduce(
              (sum, assignment) => sum + (assignment.progressPercent || 0),
              0
            ) / assignments.length
          )
        : 0;
      return [
        {
          label: 'Videos',
          value: videoCount,
          description: 'Total # of videos assigned in training path',
        },
        {
          label: 'Trainees',
          value: assignments.length,
          description: '# of users assigned to this training path',
        },
        {
          label: 'Average Progress',
          value: `${averageProgress}%`,
          description:
            'The average completion progress of all users assigned to this training path.',
        },
      ];
    }
    return [
      {
        label: 'Videos',
        value: videoCount,
        description: 'Total # of videos assigned in training path',
      },
      {
        label: 'Your progress',
        value: `${Math.round(activeAssignment?.progressPercent || 0)}%`,
        description: 'Your completion progress in this training path.',
      },
      {
        label: 'Training status',
        value: traineeStatus,
        description: 'Current training status for your assignment.',
      },
    ];
  }, [
    assignments,
    content.length,
    isAdminView,
    activeAssignment?.progressPercent,
    traineeStatus,
    trainingPath,
  ]);

  const traineePrimaryMeetingId = useMemo(() => {
    if (!content.length) {
      return null;
    }

    // Find the first video that is not 100% complete
    const firstIncompleteVideo = content.find(video => (video.progressPercent || 0) < 100);

    // If there's an incomplete video, start with it; otherwise, start with the first video
    return (firstIncompleteVideo || content[0]).id;
  }, [content]);

  const renderContentTable = () => {
    if (isLoading && !content.length) {
      return (
        <div className={classes.loaderContainer}>
          <DotsLoader />
        </div>
      );
    }

    if (!content.length) {
      return (
        <div className={classes.emptyState}>
          <EmptyFolderMessage />
        </div>
      );
    }

    return (
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Title</th>
            <th>Folder</th>
            <th>Last Updated</th>
            {(isAdminView || canTraineePlayAll) && <th />}
          </tr>
        </thead>
        <tbody>
          {content.map((item) => {
            const shouldShowPlayButton =
              isAdminView ||
              canTraineePlayAll ||
              (!isAdminView && item.isCompleted);

            return (
              <tr
                key={item.id}
                onClick={isAdminView ? () => {
                  window.open(`/my-videos/${item.id}`, '_blank', 'noopener');
                } : undefined}
                style={!isAdminView ? { cursor: 'default' } : undefined}
              >
                <td data-label="">
                  <div className={classes.itemTitleWrapper}>
                    {!isAdminView && (
                      <span
                        className={classNames(classes.statusIcon, {
                          [classes.statusIconComplete]: item.isCompleted,
                        })}
                      >
                        <img src={checkIcon} alt="" />
                      </span>
                    )}
                    <p className={classes.itemTitle}>{item.title}</p>
                  </div>
                </td>
                <td data-label="Folder">{item.folder?.name || '—'}</td>
                <td data-label="Last Updated">{formatDate(item.updatedAt)}</td>
                {shouldShowPlayButton && (
                  <td className={classes.viewCell} data-label="">
                    <button
                      type="button"
                      className={classes.playButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        const baseUrl = `/my-videos/${item.id}`;
                        const search = !isAdminView && activeAssignment?.id
                          ? `?trainingPathAssignmentId=${activeAssignment.id}`
                          : '';
                        window.open(`${baseUrl}${search}`, '_blank', 'noopener');
                      }}
                    >
                      <img src={playIcon} alt="Play" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderTraineesTable = () => {
    if (!isAdminView) {
      return null;
    }

    if (!assignments.length) {
      return (
        <div className={classes.emptyState}>
          No one has been assigned to this training path yet.
        </div>
      );
    }

    return (
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Title</th>
            <th>Status</th>
            <th>Progress</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {assignments.map((assignment) => {
            const trainee = assignment.Trainee;
            const canApprove =
              assignment.progressPercent >= 100 &&
              assignment.status !== 'approved' &&
              assignment.status !== 'canceled';
            const isCanceled = assignment.status === 'canceled';

            return (
              <tr key={assignment.id}>
                <td data-label="">
                  <div className={classes.itemTitleWrapper}>
                    <span
                      className={classNames(classes.statusIcon, {
                        [classes.statusIconComplete]:
                          Math.round(assignment.progressPercent || 0) === 100,
                      })}
                    >
                      <img src={checkIcon} alt="" />
                    </span>
                    <p className={classes.itemTitle}>
                      {trainee?.name || 'Unknown user'}
                    </p>
                  </div>
                </td>
                <td data-label="Title">{trainee?.title || '—'}</td>
                <td data-label="Status">
                        <span
                          className={classes.statusBadge}
                          data-status={getStatusToken(assignment.status)}
                        >
                          {getTrainingPathStatusLabel(assignment.status)}
                  </span>
                </td>
                <td data-label="Progress">
                  <div className={classes.progressCell}>
                    <div className={classes.progressBar}>
                      <div
                        className={classes.progressFill}
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, Math.round(assignment.progressPercent || 0))
                          )}%`,
                        }}
                      />
                    </div>
                    <span>{Math.round(assignment.progressPercent || 0)}%</span>
                  </div>
                </td>
                <td className={classes.rowActions} data-label="">
                  <Tooltip
                    text={!canApprove ? "Trainee must complete 100% of the training path" : ""}
                    position="top-left"
                    width={240}
                    show={!canApprove}
                  >
                  <FilledButton
                    isDisabled={!canApprove}
                    onClick={() => handleApproveRequest(assignment.id)}
                  >
                    Verify Training Completion
                  </FilledButton>
                  </Tooltip>
                  <OutlinedButton
                    onClick={() => isCanceled ? handleReassignRequest(assignment.id) : handleCancelRequest(assignment.id)}
                  >
                    {isCanceled ? 'Re-assign Training' : 'Cancel Training'}
                  </OutlinedButton>
                  <OutlinedButton
                    isDisabled={assignment.status === 'canceled'}
                    onClick={() => handleResetRequest(assignment.id)}
                  >
                    Reset Progress
                  </OutlinedButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <Layout hasUserBlock style={{ overflowX: 'hidden' }}>
      <div className={classes.TrainingPathDetailPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
              <Breadcrumbs
                breadcrumbsOptions={[
                  { label: 'Training Paths', to: '/training-paths' },
                  { label: trainingPath?.name || 'Details', to: '#' },
                ]}
              />
          </div>
          {(() => {
            if (isLoading && !trainingPath) {
              return (
            <div className={classes.loaderContainer}>
              <DotsLoader />
            </div>
              );
            }
            if (error?.response?.status === 403) {
              return (
                <div className={classes.emptyState}>
                  <h2>Access Denied</h2>
                  <p>You don&apos;t have access to this training path. Please contact your administrator if you believe this is an error.</p>
                </div>
              );
            }
            return (
            <>
              {!isAdminView && Math.round(activeAssignment?.progressPercent || 0) >= 100 && activeAssignment?.status !== 'approved' && (
                <div className={classes.completionBanner}>
                  <div className={classes.completionBannerContent}>
                    <h4>🎉 Congratulations on completing your training path!</h4>
                    <p>Awesome work! Your training is now pending training verification from your organization admin.</p>
                  </div>
                </div>
              )}

              {!isAdminView && activeAssignment?.status === 'approved' && (
                <div className={classes.approvedBanner}>
                  <div className={classes.approvedBannerContent}>
                    <div className={classes.trophyEmoji}>🏆</div>
                    <h4>Training Verified!</h4>
                    <div className={classes.approvedText}>
                      <div className={classes.approvedLabel}>
                        Dear <strong>{currentUser?.name || currentUser?.email || 'Trainee'}</strong>, you have successfully completed your training on
                      </div>
                      <div className={classes.approvedName}>{trainingPath?.name}</div>
                      <div className={classes.certificationDate}>
                        Your certification was issued on {activeAssignment?.updatedAt ? new Date(activeAssignment.updatedAt).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <section className={classes.headerCard}>
                <div className={classes.headerTop}>
                  <div className={classes.headerInfo}>
                    <div className={classes.statusRow}>
                      <span className={classes.statusLabel}>Status:</span>
                      <span
                        className={classes.statusBadge}
                        data-status={summaryStatusToken}
                      >
                        {summaryStatusLabel}
                      </span>
                    </div>
                    <h1>{trainingPath?.name}</h1>
                    <p className={classes.subtitle}>
                      {trainingPath?.description || 'No description provided.'}
                    </p>
                  </div>
                  <div className={classes.headerActions}>
                    {isAdminView && (
                      <FilledButton onClick={() => navigate(`/training-paths/${id}/edit`)}>
                        Edit Training Path
                      </FilledButton>
                    )}
                  </div>
                </div>

                <div className={classes.metricsGrid}>
                  {summaryMetrics.map((metric) => (
                    <div key={metric.label} className={classes.metricCard}>
                      <span className={classes.metricLabel}>{metric.label}</span>
                      <span className={classes.metricValue}>{metric.value}</span>
                      {metric.description && (
                        <p className={classes.metricDescription}>
                          {metric.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {!isAdminView && traineePrimaryMeetingId && (
                  <div className={classes.resumeRow}>
                    {(() => {
                      if (activeAssignment?.status === 'approved') {
                        return (
                          <div className={classes.certificationBadge}>
                            <div className={classes.certificationText}>
                              <div className={classes.certificationLabel}>
                                You&apos;ve been granted certification on:
                              </div>
                              <div className={classes.certificationTitle}>
                                {trainingPath?.name}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      if (Math.round(activeAssignment?.progressPercent || 0) >= 100) {
                        return (
                          <div className={classes.statusBadge}>
                            Pending Training Verification
                          </div>
                        );
                      }

                      return (
                        <FilledButton
                          onClick={async () => {
                            if (!traineePrimaryMeetingId) return;

                            // Open window immediately (synchronously) to avoid iOS Safari blocking
                            // This must happen before any async operations
                            const baseUrl = `/my-videos/${traineePrimaryMeetingId}`;
                            const newWindow = window.open(baseUrl, '_blank');

                            // If window.open was blocked, try using navigate instead
                            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                              // Fallback: navigate in same window if popup was blocked
                              navigate(`${baseUrl}${activeAssignment?.id ? `?trainingPathAssignmentId=${activeAssignment.id}` : ''}`);
                              return;
                            }

                            let search = activeAssignment?.id
                              ? `?trainingPathAssignmentId=${activeAssignment.id}`
                              : '';

                            // If we have training path assignment, check for resume position
                            if (activeAssignment?.id) {
                              try {
                                const progressData = await TrainingPathService.getSectionProgress(
                                  activeAssignment.id,
                                  traineePrimaryMeetingId
                                );

                                // Find the most recently updated section (last played)
                                if (progressData && progressData.length > 0) {
                                  const lastPlayedSection = progressData[0]; // Already sorted by updatedAt DESC

                                  if (lastPlayedSection.sectionId) {
                                    search += `${search ? '&' : '?'}resumeSection=${lastPlayedSection.sectionId}`;
                                  }
                                }
                              } catch (resumeError) {
                                // Continue without resume if there's an error
                              }
                            }

                            // Update the window location with the full URL including search params
                            const url = `${baseUrl}${search}`;
                            if (newWindow && !newWindow.closed) {
                              newWindow.location.href = url;
                            }
                          }}
                        >
                          {Math.round(activeAssignment?.progressPercent || 0) > 0
                            ? 'Resume Training'
                            : 'Start Training'}
                        </FilledButton>
                      );
                    })()}
                  </div>
                )}
              </section>

              <section className={classes.sectionCard}>
                <div className={classes.sectionHeader}>
                  <div className={classes.sectionTitle}>
                    <span className={classes.sectionIcon}>
                      <img src={checklistIcon} alt="" />
                    </span>
                    <h3>Training Content in this Path</h3>
                  </div>
                </div>
                <div className={classes.tableContainer}>
                  {renderContentTable()}
                </div>
              </section>

              {isAdminView && (
                <section className={classes.sectionCard}>
                  <div className={classes.sectionHeader}>
                    <div className={classes.sectionTitle}>
                      <span className={classes.sectionIcon}>
                        <img src={usersIcon} alt="" />
                      </span>
                      <h3>Assigned Trainees</h3>
                    </div>
                    <Tooltip
                      text="Training path status must be active in order to add a new trainee"
                      position="top-left"
                      width={300}
                      show={trainingPath?.status === TRAINING_PATH_STATUSES.INACTIVE}
                    >
                      <FilledButton
                        isDisabled={trainingPath?.status === TRAINING_PATH_STATUSES.INACTIVE}
                        onClick={() => setIsAssignModalOpen(true)}
                      >
                        Add Trainee
                      </FilledButton>
                    </Tooltip>
                  </div>
                  <div className={classes.tableContainer}>
                  {renderTraineesTable()}
                  </div>
                </section>
              )}
            </>
            );
          })()}
        </div>
      </div>

      <ConfirmActionModal
        title={isReassigning ? "Re-assign training?" : "Cancel training?"}
        message={isReassigning ? "This will restore the trainee's access to the training path and set the status to Assigned." : "This trainee will lose access to the training path."}
        show={isCancelModalVisible}
        handleClose={handleCancelClose}
        onConfirm={isReassigning ? handleReassignConfirm : handleCancelConfirm}
      />
      <ConfirmActionModal
        title="Reset trainee progress?"
        message="Resets viewing progress and requires user to retake training path. This cannot be undone."
        show={isResetModalVisible}
        handleClose={handleResetClose}
        onConfirm={handleResetConfirm}
      />
      <ConfirmActionModal
        title="Verify Training Completion?"
        message={`Are you sure you want to verify training completion for <strong>${approveTraineeName}</strong>? Their training will be certified as of ${new Date().toLocaleDateString()} and they will be notified by email.`}
        show={isApproveModalVisible}
        handleClose={handleApproveClose}
        onConfirm={handleApproveConfirm}
      />
      <TrainingPathAssignModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        users={organizationUsers}
        trainingPathOptions={trainingPathOptions}
        onAssign={handleModalAssign}
        preSelectedTrainingPath={trainingPath?.id}
        isSinglePathSelection
      />
    </Layout>
  );
}

